import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from 'react-toastify';
import { processServerError } from './utils/helpers/error.helper';
import { FeatureFlagsSettings, GeneralSettings, OidcSettings, SettingsClient, StripeSettings, UrlSettings } from './swagger-clients/s365-dashboard-v2-web-clients.service';

import { FluentProvider, Theme, webDarkTheme, webLightTheme } from '@fluentui/react-components';
import { RoutesDefinitions } from './RoutesDefinitions';
import { DwsimProLicenseProvider } from './contexts/DwsimProLicenseContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

export var settings: UrlSettings | undefined = undefined;
export var generalSettings: GeneralSettings | undefined = undefined;
export var featureFlagsSettings: FeatureFlagsSettings | undefined = undefined;
export var stripeSettings: StripeSettings | undefined = undefined;
export var oidcSettings: OidcSettings | undefined = undefined;

const getCurrentWebUrl = () => {
  return window.location.origin.indexOf("https://localhost:44485") > -1
    ? 'https://localhost:9401' 
    : window.location.origin;
}

function App() {

  const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(true);

  const [currentTheme, setCurrentTheme] = useState<Theme>(webLightTheme);

  const navigate = useNavigate();

  useEffect(() => {
    onComponentInit();
  }, []);

  const onComponentInit = async () => {
    const s365Theme = window.localStorage.getItem("s365-theme");
    if (s365Theme == "dark") {
      setCurrentTheme(webDarkTheme);
    }
    await getConfiguration();
  }

  const onChangeTheme = (theme: Theme, isDark: boolean) => {
    setCurrentTheme(theme);
    window.localStorage.setItem("s365-theme", isDark ? "dark" : "light");
  }

  const getConfiguration = async () => {
    try {
      setIsLoadingConfig(true);
      
      // Skip fetching if settings were already loaded in bootstrap
      if (oidcSettings !== undefined) {
        setIsLoadingConfig(false);
        return;
      }

      let baseUrl = getCurrentWebUrl();
      const client = new SettingsClient(baseUrl);
      const settingsResp = await client.getSettings();
      settings = settingsResp?.urlSettings;
      generalSettings = settingsResp?.generalSettings;
      featureFlagsSettings = settingsResp?.featureFlagsSettings;
      stripeSettings = settingsResp?.stripeSettings;
      oidcSettings = settingsResp?.oidcSettings;

     // console.log("generalSettings", generalSettings);
    } catch (error) {
      processServerError(error, undefined, "An error occurred while loading configuration.");
    }
    finally {
      setIsLoadingConfig(false);
    }
  }

  if (!!isLoadingConfig) return <span></span>;

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false, // default: true
      },
    },
  })

  return (
    <QueryClientProvider client={queryClient}>
      <FluentProvider theme={currentTheme}>
        <DwsimProLicenseProvider>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={true}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            style={{ zIndex: 1000001 }}
            draggable
            pauseOnHover
          />
          <RoutesDefinitions currentTheme={currentTheme} onChangeTheme={onChangeTheme} />
        </DwsimProLicenseProvider>
      </FluentProvider>
    </QueryClientProvider >
  );
}

export default App;
export { getCurrentWebUrl };